import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Building2, Clock, ExternalLink, Flame, MapPin, Phone, Store, Users } from 'lucide-react';
import BeltImage from 'app/javascript/components/shared/BeltImage';
import ProfileAvatar from 'app/javascript/components/shared/ProfileAvatar';
import ScheduleGrid from 'app/javascript/components/companies/ScheduleGrid';
import ScheduleList from 'app/javascript/components/companies/ScheduleList';
import StorePreview from 'app/javascript/components/store/StorePreview';
import { Avatar, AvatarFallback, AvatarImage } from 'app/javascript/components/ui/avatar';
import { Badge } from 'app/javascript/components/ui/badge';
import { Button } from 'app/javascript/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from 'app/javascript/components/ui/empty';
import { useAuth } from 'app/javascript/contexts/AuthContext';
import { useMyCheckInStats } from 'app/javascript/queries/useClassCheckIns';
import { trackEvent } from 'app/javascript/firebase';
import { companiesService, PublicCompanyDetail } from 'app/javascript/services/companies';
import { BeltRankValue, getBeltLabel, getBeltTheme, normalizeBeltRank } from 'app/javascript/services/belts';
import { ROUTES } from 'app/javascript/constants/routes';
import { ScheduleHighlight, getHighlightedScheduleIds } from 'app/javascript/utils/scheduleHelpers';
import { buildWhatsAppUrl } from 'app/javascript/utils/whatsapp';

// Section heading: uppercase Bebas label + optional right accessory.
// Editorial / fight-card vibe — no card chrome required.
function SectionLabel({ label, accessory }: { label: string; accessory?: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-baseline justify-between gap-4">
      <h2 className="font-bebas text-3xl uppercase tracking-[0.04em] text-foreground sm:text-4xl">{label}</h2>
      {accessory}
    </div>
  );
}

export default function CompanyPublicPage() {
  const { slug } = useParams({ from: '/bjj/$slug' });
  const { currentUser } = useAuth();
  const [company, setCompany] = useState<PublicCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinRequested, setJoinRequested] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<ScheduleHighlight>({ activeIds: [], nextUpcomingId: null });

  const isAcceptedMember = membershipStatus === 'accepted';
  const whatsappUrl = buildWhatsAppUrl(company?.phone, '');
  const { data: statsData } = useMyCheckInStats(slug, !!currentUser && isAcceptedMember);

  useEffect(() => {
    if (!company?.class_schedules?.length) return;
    const compute = () => {
      const next = getHighlightedScheduleIds(company.class_schedules);
      setHighlight((prev) => {
        const sameActive = prev.activeIds.length === next.activeIds.length && prev.activeIds.every((id, i) => id === next.activeIds[i]);
        if (sameActive && prev.nextUpcomingId === next.nextUpcomingId) return prev;
        return next;
      });
    };
    compute();
    const interval = setInterval(compute, 60_000);
    return () => clearInterval(interval);
  }, [company]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const data = await companiesService.getPublic(slug);
        setCompany(data);
        if (currentUser) {
          try {
            const authData = await companiesService.get(slug);
            if (authData.my_membership) setMembershipStatus(authData.my_membership.status);
          } catch {
            // user may not have access, ignore
          }
        }
        trackEvent('company_viewed', {
          company_id: data.id.toString(),
          company_name: data.name,
          company_slug: slug,
          member_count: data.members.length,
        });
      } catch {
        setError('Equipe não encontrada');
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchCompany();
  }, [slug]);

  const handleRequestJoin = async () => {
    if (!company) return;
    setJoining(true);
    try {
      await companiesService.requestJoin(company.slug);
      setJoinRequested(true);
      setMembershipStatus('pending');
    } catch (err) {
      console.error('Failed to request join', err);
    } finally {
      setJoining(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-foreground">Carregando...</div>;
  if (error) return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!company) return null;

  const showJoinButton =
    currentUser &&
    !joinRequested &&
    membershipStatus !== 'pending' &&
    membershipStatus !== 'accepted' &&
    !company.members.some((m) => m.id === currentUser.railsId);

  const showPendingBadge = joinRequested || membershipStatus === 'pending';
  // Backend flag from the company payload; StorePreview fetches the catalog only
  // when this is true.
  const showStore = company.has_store;

  const location = [company.city, company.state].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* === HERO — fight-card style anchor === */}
      <header className="relative overflow-hidden border-b border-border/40">
        {/* Editorial atmosphere: gold wash + tatami grid */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/[0.07] via-background to-background" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(90deg, #fbbf24 1px, transparent 1px), linear-gradient(180deg, #fbbf24 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div
          className="pointer-events-none absolute -right-24 -top-24 size-[420px] rounded-full bg-yellow-400/10 blur-[120px]"
        />

        <div className="relative mx-auto max-w-5xl px-4 pb-12 pt-12 sm:px-6 sm:pb-16 sm:pt-20">
          <div className="flex items-start gap-6 sm:gap-8">
            <Avatar className="size-20 shrink-0 rounded-2xl bg-card shadow-xl ring-1 ring-border/50 sm:size-24">
              {company.logo_url ? (
                <AvatarImage src={company.logo_url} alt={company.name} className="object-cover" />
              ) : null}
              <AvatarFallback className="rounded-2xl bg-card">
                <Building2 className="size-9 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.32em] text-yellow-400/80">
                Equipe · BlackBelts
              </p>
              <h1 className="font-bebas text-5xl leading-[0.95] tracking-tight text-foreground sm:text-6xl md:text-7xl">
                {company.name}
              </h1>
              {company.description && (
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
                  {company.description}
                </p>
              )}
            </div>

            <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end sm:gap-2">
              {showStore && (
                <Button
                  asChild
                  variant="outline"
                  className="border-yellow-400/40 font-bebas text-base uppercase tracking-wide text-yellow-400 hover:border-yellow-400 hover:bg-yellow-400/10 hover:text-yellow-300"
                >
                  <Link to={ROUTES.COMPANY_STORE} params={{ slug: company.slug }}>
                    <Store className="size-4" />
                    Loja
                  </Link>
                </Button>
              )}
              {showJoinButton && (
                <Button
                  onClick={handleRequestJoin}
                  disabled={joining}
                  className="bg-yellow-400 font-bebas text-base uppercase tracking-wide text-slate-900 shadow-lg shadow-yellow-400/20 hover:bg-yellow-300"
                >
                  {joining ? 'Enviando...' : 'Solicitar entrada'}
                </Button>
              )}
              {showPendingBadge && (
                <Badge className="border-transparent bg-yellow-400/10 px-3 py-1.5 text-yellow-400">
                  <Clock className="size-3" />
                  Pendente
                </Badge>
              )}
            </div>
          </div>

          {/* Stat strip — pipe-separated dossier line */}
          <dl className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border/40 pt-6 text-sm">
            <Stat icon={Users} label="Membros" value={company.members.length.toString()} />
            {location && <Stat icon={MapPin} label="Localização" value={location} />}
            {company.address && <Stat icon={MapPin} label="Endereço" value={company.address} />}
            {company.phone && whatsappUrl && (
              <Stat
                icon={Phone}
                label="WhatsApp"
                value={
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Conversar com ${company.name} no WhatsApp: ${company.phone}`}
                    className="inline-flex min-h-11 items-center rounded-lg text-yellow-400 underline underline-offset-4 transition-colors hover:text-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
                  >
                    {company.phone}
                  </a>
                }
              />
            )}
            {company.website_url && (
              <Stat
                icon={ExternalLink}
                label="Site"
                value={
                  <a
                    href={company.website_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-yellow-400 transition-colors hover:text-yellow-300"
                  >
                    {company.website_url}
                  </a>
                }
              />
            )}
            {isAcceptedMember && statsData && statsData.month_count > 0 && (
              <Stat
                icon={Flame}
                iconClass="text-orange-400"
                label="Este mês"
                value={
                  <>
                    <span className="font-semibold text-foreground">{statsData.month_count}</span>{' '}
                    {statsData.month_count === 1 ? 'treino' : 'treinos'}
                  </>
                }
              />
            )}
          </dl>

          {/* Mobile CTA below stats */}
          {(showStore || showJoinButton || showPendingBadge) && (
            <div className="mt-6 flex flex-col gap-2 sm:hidden">
              {showStore && (
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-yellow-400/40 text-yellow-400 hover:border-yellow-400 hover:bg-yellow-400/10 hover:text-yellow-300"
                >
                  <Link to={ROUTES.COMPANY_STORE} params={{ slug: company.slug }}>
                    <Store className="size-4" />
                    Ver loja
                  </Link>
                </Button>
              )}
              {showJoinButton && (
                <Button
                  onClick={handleRequestJoin}
                  disabled={joining}
                  className="w-full bg-yellow-400 text-slate-900 hover:bg-yellow-300"
                >
                  {joining ? 'Enviando...' : 'Solicitar entrada'}
                </Button>
              )}
              {showPendingBadge && (
                <Badge className="self-start border-transparent bg-yellow-400/10 text-yellow-400">
                  <Clock className="size-3" />
                  Pendente
                </Badge>
              )}
            </div>
          )}
        </div>
      </header>

      {/* === CONTENT === */}
      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
        {company.class_schedules && company.class_schedules.length > 0 && (
          <section className="mb-16">
            <SectionLabel label="Horários" />
            <div className="hidden lg:block">
              <ScheduleGrid schedules={company.class_schedules} highlight={highlight} />
            </div>
            <div className="lg:hidden">
              <ScheduleList schedules={company.class_schedules} highlight={highlight} />
            </div>
          </section>
        )}

        {showStore && (
          <section className="mb-16">
            <StorePreview slug={company.slug} />
          </section>
        )}

        <section>
          <SectionLabel
            label="Equipe"
            accessory={
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {company.members.length} {company.members.length === 1 ? 'membro' : 'membros'}
              </span>
            }
          />

          {company.members.length === 0 ? (
            <Empty className="border-0 bg-transparent">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Users />
                </EmptyMedia>
                <EmptyTitle>Sem membros ainda</EmptyTitle>
                <EmptyDescription>Nenhum membro cadastrado nesta equipe.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {company.members.map((member) => {
                const rank = member.current_belt?.rank || 'white';
                const beltLabel = getBeltLabel(rank as BeltRankValue);
                const beltTheme = getBeltTheme(normalizeBeltRank(rank));
                return (
                  <Link
                    key={member.id}
                    to={ROUTES.PUBLIC_PROFILE}
                    params={{ slug: member.slug }}
                    className="group relative flex flex-col items-center"
                  >
                    {/* Photo with belt-color border */}
                    <div
                      className="inline-block rounded-full transition-transform duration-300 group-hover:scale-[1.03]"
                      style={{ boxShadow: `0 0 0 3px ${beltTheme.borderHex}` }}
                    >
                      <ProfileAvatar
                        photoUrl={member.photo_url}
                        name={member.name}
                        rank={rank}
                        size="sm"
                      />
                    </div>

                    <h3 className="mt-4 text-center font-bebas text-lg uppercase leading-tight tracking-wide text-foreground transition-colors group-hover:text-yellow-400">
                      {member.name}
                    </h3>

                    <span className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      <BeltImage rank={rank} size="xs" />
                      {beltLabel}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

interface StatProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  iconClass?: string;
}

function Stat({ icon: Icon, label, value, iconClass }: StatProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`size-3.5 shrink-0 ${iconClass ?? 'text-yellow-400/80'}`} />
      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}
